import { defineAction, object, string, number, array, enumerated } from '~/prix'
import insightListAction from './insightList.action.json' assert { type: 'json' }
import { Insight } from '~/packages/variable/variables.typing'
import { SpaceDelimitationGeoStandard } from '~/packages/variable/spaceTime.types'
import { spaceForPoint } from '~/prix/types/presets/space'
import z from 'zod'

export interface InsightFromVariable
  extends Pick<
    Insight,
    | 'id'
    | 'type'
    | 'key'
    | 'startDate'
    | 'endDate'
    | 'title'
    | 'description'
    | 'relevance'
    | 'alert'
  > {
  variableKey: string
  variableTitle: string
  variableLabel: string | null
  conclusionSpace: SpaceDelimitationGeoStandard | null
}

const insightListDefinition = defineAction({
  ...insightListAction,

  input: object({
    search: string().isOptional(),
    delimitation: string().isOptional(),
    variableTitle: string().isOptional(),
    productName: string().isOptional(),
    productCode: string().isOptional(),
    alertType: array(string()).isOptional(),
    alertPreset: string().isOptional(),
    portfolio: string().isOptional(),
    sortBy: string().isOptional(),
    sortOrder: string().isOptional(),
    startIndex: number().isOptional(),
    endIndex: number().isOptional(),
  }),
  output: object({
    items: array(
      object({
        id: string(),
        key: string(),
        type: string(),
        title: string(),
        description: string(),
        relevance: string(),
        alert: string(),

        variableKey: string(),
        variableTitle: string(),
        variableLabel: string().isOptional(),

        conclusionSpace: spaceForPoint().isOptional(),
        variableFieldDisplay: string(),
        courseCode: string().isOptional(),
        productCode: string().isOptional(),
        productName: string(),
        portfolioName: string().isOptional(),
        actions: array(string()).isOptional(),
        visualizations: array(string()).isOptional(),
      }),
    ),
    total: number(),
    portfolio: array(
      object({
        code: string(),
        name: string(),
      }),
    ).isOptional(),
  }),
  cache: input => {
    if (input.search) {
      return {}
    }

    return {
      back: {},
      front: {
        // 5 days
        expirationMsForFallback: 1000 * 60 * 60 * 24 * 5,
        // 5 minutes
        expirationMsForUpdate: 1000 * 60 * 5,
      },
    }
  },
})

export default insightListDefinition

export const inputZod = z.object({
  search: z.string().optional().describe('Termos de busca para filtrar os insights.'),
  sortBy: z
    .enum(['description', 'state', 'alertBadge', 'alertType', 'variable', 'solutionPortfolio'])
    .optional()
    .describe('Campo de ordenação'),
  sortOrder: z.enum(['asc', 'desc']).optional().describe('Direção da ordenação'),
  delimitation: z
    .enum([
      'Brasil',
      'AC',
      'AL',
      'AP',
      'AM',
      'BA',
      'CE',
      'DF',
      'ES',
      'GO',
      'MA',
      'MT',
      'MS',
      'MG',
      'PA',
      'PB',
      'PR',
      'PE',
      'PI',
      'RJ',
      'RN',
      'RS',
      'RO',
      'RR',
      'SC',
      'SP',
      'SE',
      'TO',
    ])
    .optional()
    .describe('Delimitação geográfica, como a sigla de alguma UF ou "Brasil".'),
  productName: z.string().optional().describe('Chave da variável para filtrar insights.'),
  productCode: z.string().optional().describe('Código do produto para filtrar insights.'),
  alertType: z
    .array(z.string())
    .optional()
    .describe('Array de tipos de alerta para filtro múltiplo.'),
  alertPreset: z
    .enum([
      'with-alert',
      'without-alert',
      'notification',
      'outbreak',
      'urgent',
      'emergent',
      'new',
      'decline',
      'informative',
      'increase',
      'decrease',
      'sparse',
    ])
    .optional()
    .describe(
      'Preset de alerta: with-alert (qualquer alerta), ou presets específicos como increase, decrease, etc.',
    ),
  portfolio: z
    .string()
    .optional()
    .describe(
      'Filtro de portfólio: in-portfolio (tem portfólio), outside-portfolio (sem portfólio), ou nome específico do portfólio.',
    ),
  startIndex: z.number().optional().default(0),
  endIndex: z.number().optional().default(10),
})
